import { useEffect, useRef, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { ChevronDown, Download } from "lucide-react";

export default function ChatWindow({ messages, isLoading }) {
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [lastMessageLength, setLastMessageLength] = useState(0);

  // --- Scrolling and layout hooks ---

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    setIsAutoScrolling(true);
  };

  const isAtBottom = () => {
    const el = containerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  };

  useEffect(() => {
    const el = containerRef.current;
    const handleScroll = () => {
      if (!el) return;
      const atBottom = isAtBottom();
      setShowScrollButton(!atBottom);
      if (!atBottom) setIsAutoScrolling(false);
    };
    el?.addEventListener("scroll", handleScroll);
    return () => el?.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    //console.log("message", messages);
    const currentMessageLength = messages.reduce(
      (acc, msg) => acc + msg.content.length,
      0
    );
    if (
      currentMessageLength > lastMessageLength &&
      (isAutoScrolling || isAtBottom())
    ) {
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
        10
      );
    }
    setLastMessageLength(currentMessageLength);
  }, [messages, lastMessageLength, isAutoScrolling]);

  const preprocessContent = (content) => {
    if (!content) return "";

    let processed = content;

    processed = processed.replace(
      /([.!?]\s*)([📐🧮🔍🧩⚠️🔑✏️📝💡🔢👀🔗❗📏⚡])/g,
      "$1\n\n$2"
    );
    processed = processed.replace(
      /(\S)(\s*)([📐🧮🔍🧩⚠️🔑✏️📝💡🔢👀🔗❗📏⚡])/g,
      "$1\n\n$3"
    );
    processed = processed.replace(
      /^([^#\n]*?)([📐🧮🔍🧩⚠️🔑✏️📝💡🔢👀🔗❗📏⚡])/gm,
      "$1\n\n$2"
    );
    processed = processed.replace(/#{4,}/g, "\n\n");
    processed = processed.replace(
      /^(?:new_query|continuous_query)+\s*###\s*(.*)/i,
      "# $1"
    );
    processed = processed.replace(/^(?:continuous_query|new_query|#)+\s*/i, "");
    processed = processed.replace(/([.?!])\s*(#{1,6}\s)/g, "$1\n\n$2");
    processed = processed.replace(
      /([^\n])\n(^\s*([*+-]|\d+\.)\s)/gm,
      "$1\n\n$2"
    );
    processed = processed.replace(/^(#{1,6})([^\s#])/gm, "$1 $2");
    processed = processed.replace(/^(\s*[*+-])([^\s])/gm, "$1 $2");
    processed = processed.replace(/^(\s*\d+\.)([^\s])/gm, "$1 $2");
    processed = processed.replace(/(#{1,6})\s*[*]+\s*/g, "$1 ");
    processed = processed.replace(/\*\*\s*(.*?)\s*\*\*/g, "**$1**");
    processed = processed.replace(/(?<!\*)\*\s+(.*?)\s+\*(?!\*)/g, "*$1*");
    processed = processed.replace(/^(\s*)\d+\.\s/gm, "$1⭐ ");
    processed = processed.replace(/\)\*\*(?=[A-Z][a-z])/g, ")\n\n");
    processed = processed.replace(/\n{3,}/g, "\n\n");
    processed = processed.replace(/([#-])\1{3,}/g, "\n\n");
    processed = processed.replace(/([a-z])([A-Z])/g, "$1 $2");
    processed = processed.replace(/([a-zA-Z])-([A-Z])/g, "$1: $2");

    processed = processed.replace(
      /(https?:\/\/[^\s]+)/g,
      (url) => `[Click to watch](${url})`
    );

    return processed.trim();
  };

  const fixMathErrors = (content) => {
    if (!content) return "";

    let processed = content;

    processed = processed.replace(/\\\(([\s\S]*?)\\\)/g, "$$$1$");
    processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, "$$$$$1$$$$");
    processed = processed.replace(
      /(?<!\\)(\$\$?)([\s\S]+?)\1/g,
      (match, delimiter, mathExpression) => {
        const singleLineExpression = mathExpression
          .replace(/\s*\n\s*/g, " ")
          .trim();
        return `${delimiter}${singleLineExpression}${delimiter}`;
      }
    );

    return processed;
  };

  const markdownComponents = useMemo(
    () => ({
      h1: ({ children }) => (
        <h1 className="text-md font-bold mt-8 mb-6 pb-3 border-b-2 border-blue-500 text-white leading-tight">
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2 className="text-xl font-semibold mt-7 mb-4 text-blue-100 leading-tight">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-xl font-semibold mt-6 mb-3 text-blue-200 leading-tight">
          {children}
        </h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-lg font-semibold mt-5 mb-2 text-gray-100 leading-tight">
          {children}
        </h4>
      ),
      h5: ({ children }) => (
        <h5 className="text-base font-semibold mt-4 mb-2 text-gray-200 leading-tight">
          {children}
        </h5>
      ),
      h6: ({ children }) => (
        <h6 className="text-sm font-medium mt-3 mb-1 text-gray-300 leading-tight">
          {children}
        </h6>
      ),
      p: ({ children }) => (
        <p className="mb-4 leading-7 text-gray-100 text-base">{children}</p>
      ),
      ul: ({ children }) => (
        <ul className="mb-6 mt-2 ml-6 space-y-2 list-disc list-outside text-gray-100">
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className="mb-6 mt-2 ml-6 space-y-2 list-decimal list-outside text-gray-100">
          {children}
        </ol>
      ),
      li: ({ children }) => (
        <li className="leading-7 text-gray-100 pl-2">{children}</li>
      ),
      blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-blue-400 bg-slate-800/60 pl-6 py-4 my-6 italic text-blue-100 rounded-r-lg shadow-inner">
          {children}
        </blockquote>
      ),
      code: ({ node, inline, className, children, ...props }) => {
        if (inline) {
          return (
            <code
              className="bg-slate-800 text-blue-300 px-2 py-1 rounded-md text-sm font-mono border border-slate-600"
              {...props}
            >
              {children}
            </code>
          );
        }

        const language = className?.replace("language-", "") || "";
        return (
          <div className="my-6 rounded-lg overflow-hidden border border-slate-600 shadow-lg">
            {language && (
              <div className="bg-slate-800 px-4 py-2 text-xs text-slate-300 font-medium border-b border-slate-600 uppercase tracking-wider">
                {language}
              </div>
            )}
            <pre className="bg-slate-900 p-5 overflow-x-auto">
              <code
                className="text-slate-100 text-sm font-mono leading-relaxed"
                {...props}
              >
                {children}
              </code>
            </pre>
          </div>
        );
      },
      pre: ({ children }) => <div className="my-6">{children}</div>,
      hr: () => (
        <div className="my-8 flex items-center justify-center">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-400 to-transparent"></div>
        </div>
      ),
      strong: ({ children }) => (
        <strong className="font-semibold text-white">{children}</strong>
      ),
      em: ({ children }) => (
        <em className="italic text-blue-200">{children}</em>
      ),
      a: ({ href, children }) => (
        <a
          href={href}
          className="text-blue-400 hover:text-blue-300 underline underline-offset-2 decoration-blue-400/50 hover:decoration-blue-300 transition-all duration-200"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      ),
      img: ({ src, alt }) => (
        <div className="my-6 text-center">
          <img
            src={src}
            alt={alt}
            className="max-w-full h-auto rounded-lg shadow-xl border border-slate-600 mx-auto"
          />
          {alt && <p className="text-sm text-slate-400 mt-2 italic">{alt}</p>}
        </div>
      ),
      table: ({ children }) => (
        <div className="my-6 overflow-x-auto rounded-lg border border-slate-600 shadow-lg">
          <table className="min-w-full border-collapse">{children}</table>
        </div>
      ),
      thead: ({ children }) => (
        <thead className="bg-slate-800">{children}</thead>
      ),
      tbody: ({ children }) => (
        <tbody className="bg-slate-900/50">{children}</tbody>
      ),
      tr: ({ children }) => (
        <tr className="border-b border-slate-700 hover:bg-slate-800/40 transition-colors duration-150">
          {children}
        </tr>
      ),
      th: ({ children }) => (
        <th className="border-r border-slate-600 px-4 py-3 text-left font-semibold text-white bg-slate-800/80">
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="border-r border-slate-700 px-4 py-3 text-slate-100">
          {children}
        </td>
      ),
    }),
    []
  );

  const TypingIndicator = () => (
    <div className="flex items-center space-x-3 text-slate-400 py-4">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
        <div
          className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
          style={{ animationDelay: "0.15s" }}
        ></div>
        <div
          className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
          style={{ animationDelay: "0.3s" }}
        ></div>
      </div>
      <span className="text-sm font-medium">Thinking...</span>
    </div>
  );

  return (
    <main
      ref={containerRef}
      className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6 relative scroll-smooth bg-slate-900"
    >
      {messages.map((msg, i) => (
        <div
          key={i}
          className={`px-8 py-6 rounded-2xl shadow-2xl transition-all duration-300 ${
            msg.role === "user"
              ? "bg-gradient-to-br from-blue-900/90 to-blue-800/90 border border-blue-600/50 backdrop-blur-sm ml-auto max-w-3xl"
              : "bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 backdrop-blur-sm mr-auto max-w-4xl"
          }`}
        >
          <div className="prose prose-invert prose-lg max-w-none">
            {msg.content && msg.content.trim() ? (
              <div className="text-slate-100">
                <ReactMarkdown
                  remarkPlugins={[remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  components={markdownComponents}
                >
                  {fixMathErrors(preprocessContent(msg.content))}
                </ReactMarkdown>

                {msg.imageUrl && (
                  <div className="mt-4 flex justify-center relative">
                    {/* Image */}
                    <a
                      href={msg.imageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative inline-block"
                    >
                      <img
                        src={msg.imageUrl}
                        alt="Generated content"
                        className="w-64 md:w-80 rounded-xl shadow-lg border border-slate-600 object-contain"
                      />

                      {/* Download button (overlay) */}
                      <a
                        href={msg.imageUrl}
                        download={`image-${Date.now()}.png`}
                        className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition"
                        title="Download image"
                        onClick={(e) => e.stopPropagation()} // prevent opening image on click
                      >
                        <Download size={18} />
                      </a>
                    </a>
                  </div>
                )}

                {/* WebLinks */}
                {msg.webLinks && msg.webLinks.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">Web Links:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {msg.webLinks.map((link, idx) => (
                        <li key={idx}>
                          <a
                            href={link.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:underline"
                          >
                            {link.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* YouTube Links */}
                {msg.youtubeLinks && msg.youtubeLinks.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">YouTube Links:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {msg.youtubeLinks.map((video, idx) => (
                        <li key={idx}>
                          <a
                            href={video.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-red-400 hover:underline"
                          >
                            {video.title.replace(/^Video:\s*/i, "")}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              msg.role === "assistant" && isLoading && <TypingIndicator />
            )}
          </div>
        </div>
      ))}

      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-24 sm:bottom-24  sm:left-[58%] left-[50%]  -translate-x-[40%] z-50 bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full shadow-md"
        >
          <ChevronDown className="w-5 h-5" />
        </button>
      )}

      <div ref={bottomRef} />
    </main>
  );
}
